import {
  FC,
  useState,
  useEffect,
  createElement,
  Fragment,
  CSSProperties,
  useLayoutEffect,
  ReactElement,
} from 'react';
import { Button, ButtonProps, ButtonVariant } from '@sas/nova-commons/components/Button';
import { embeddedBundle } from './locale/embeddedI18N';
import { authorize, getConnection, _serverHackOrAuthorized } from './authorization';
import { getAuthUrl } from './authorizeImpl';
import { AuthenticationType, AuthorizationStatus } from './types';
import { Loading } from './Loading';
import { DesignTokens, useDesignTokens } from '@sas/nova-commons/theme';

const timeoutMS = 250;

function popupWindow(server: string, title: string) {
  const state = checkPopupState();
  if (state === 'open') {
    if (popupServer === server) {
      globalPopup?.focus();
      return;
    }
    closePopup();
  }
  const top = window.outerHeight / 2 + window.screenY - 600 / 2;
  const left = window.outerWidth / 2 + window.screenX - 700 / 2;
  const popup = window.open(
    getAuthUrl(server),
    title,
    `width=700,height=600,top=${top},left=${left}`
  );
  if (popup) {
    startPopupIntervalCheck(server, popup);
  }
  return popup;
}

export type LogonProps = {
  url: string;
  authenticationType?: AuthenticationType;
  onError?: (error: string) => void;
  buttonTokens?: ButtonProps['tokens'];
  style?: CSSProperties;
  loadingFallback?: () => ReactElement<any> | null;
};

let intervalCheckId: number | undefined;
let popupServer: string | undefined;
let globalPopup: Window | null = null;
let messageCb: ((event: MessageEvent) => void) | undefined;

function closePopup() {
  if (globalPopup) {
    globalPopup.close();
    globalPopup = null;
  }
  if (messageCb) {
    window.removeEventListener('message', messageCb);
    messageCb = undefined;
  }
  if (intervalCheckId !== undefined) {
    clearInterval(intervalCheckId);
    intervalCheckId = undefined;
    popupServer = undefined;
  }
}

function checkPopupState() {
  if (!globalPopup || globalPopup.closed) {
    closePopup();
    return 'closed';
  }
  if (!popupServer) {
    if (globalPopup && !globalPopup.closed) {
      closePopup();
    }
    return 'closed';
  }
  if (_serverHackOrAuthorized(popupServer)) {
    closePopup();
    return 'closed';
  }
  globalPopup.postMessage('register', '*');
  return 'open';
}

function startPopupIntervalCheck(server: string, popup: Window) {
  checkPopupState();
  if (intervalCheckId) {
    return;
  }
  popupServer = server;
  globalPopup = popup;

  messageCb = (event: MessageEvent) => {
    if (server.indexOf(event.origin) !== -1 && event.data && event.data.logonStatus) {
      authorize(server);
    }
  };
  window.addEventListener('message', messageCb);
  // @ts-ignore node type map leakage
  intervalCheckId = setInterval(() => {
    checkPopupState();
  }, timeoutMS);
}

export const Logon: FC<LogonProps> = (props) => {
  const { url, authenticationType = 'credentials' } = props;
  const [auth, setAuth] = useState<AuthorizationStatus>(
    () => getConnection(url, authenticationType).currentItem
  );
  useEffect(() => {
    return getConnection(url, authenticationType).subscribe((state) => setAuth(state));
  }, [url, authenticationType]);

  // checkPopupState on unmount
  useEffect(
    () => () => {
      checkPopupState();
    },
    []
  );

  const dt = useDesignTokens();
  const error = auth.error;
  const onError = props.onError;
  useLayoutEffect(() => {
    if (error) {
      onError?.(error);
    }
  }, [error, onError]);

  if (auth.status === 'authorized' && props.children) {
    return <Fragment>{props.children}</Fragment>;
  } else if (auth.status !== 'credentials-invalid') {
    return props.loadingFallback ? props.loadingFallback() : <Loading />;
  } else {
    return (
      <div
        style={{
          display: 'flex',
          flexShrink: 1,
          flexDirection: 'column',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: dt.enableNovaTokens
            ? dt.backgroundsNova.panes.bg
            : dt.backgrounds.panes.bg,
          ...props.style,
        }}
      >
        <div style={{ color: props.style?.color ? props.style?.color : getTextColor(dt) }}>
          {embeddedBundle['logon.message.msg']()}
        </div>
        <Button
          text={embeddedBundle['logon.button.msg']()}
          variant={ButtonVariant.Primary}
          onPress={() => {
            popupWindow(props.url, 'SAS Logon');
          }}
          tokens={props.buttonTokens}
        />
      </div>
    );
  }
};

// Forked from BusyStateIndicator
function getTextColor(dt: DesignTokens) {
  switch (dt.themeVariant) {
    case 'dark':
    case 'high-contrast':
      return dt.c.white;
    default:
      return dt.c.n_3;
  }
}
